/**
 * Dropzone Overlay
 * Visual feedback when dragging files over the canvas
 */

import React from 'react';
import { Image, FileJson, Clapperboard, FileBox, Video, Volume2 } from 'lucide-react';
import type { DraggedFileType } from '@/hooks/useCanvasDropzone';

interface DropzoneOverlayProps {
  fileType: DraggedFileType;
}

const FILE_TYPE_INFO = {
  image: {
    icon: Image,
    label: 'Drop to add image',
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/50',
  },
  video: {
    icon: Video,
    label: 'Drop to add video',
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/10',
    borderColor: 'border-pink-500/50',
  },
  audio: {
    icon: Volume2,
    label: 'Drop to add audio',
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/10',
    borderColor: 'border-cyan-500/50',
  },
  lottie: {
    icon: FileJson,
    label: 'Drop to add Lottie animation',
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/50',
  },
  rive: {
    icon: Clapperboard,
    label: 'Drop to add Rive animation',
    color: 'text-orange-400',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/50',
  },
  slate: {
    icon: FileBox,
    label: 'Drop to load template',
    color: 'text-green-400',
    bgColor: 'bg-green-500/10',
    borderColor: 'border-green-500/50',
  },
  unknown: {
    icon: FileBox,
    label: 'Drop file',
    color: 'text-fg-muted',
    bgColor: 'bg-white/5',
    borderColor: 'border-white/20',
  },
};

export function DropzoneOverlay({ fileType }: DropzoneOverlayProps) {
  const info = FILE_TYPE_INFO[fileType || 'unknown'];
  const Icon = info.icon;

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-navy-dark/80 backdrop-blur-sm" />

      {/* Drop zone indicator */}
      <div
        className={`
          relative flex flex-col items-center justify-center gap-4 p-12
          rounded-2xl border-2 border-dashed
          ${info.bgColor} ${info.borderColor}
          animate-in fade-in zoom-in-95 duration-200
        `}
      >
        <div className={`p-4 rounded-full ${info.bgColor}`}>
          <Icon size={48} className={info.color} strokeWidth={1.5} />
        </div>
        <span className={`text-lg font-medium ${info.color}`}>
          {info.label}
        </span>
        <span className="text-sm text-fg-muted">
          Release to add to canvas
        </span>
      </div>
    </div>
  );
}
